import { getAnonymousId, markResumeCompleted, readResumeState, saveResumeState } from './activityIntelligence';
import { enqueueResumeSync } from './resumeSync';

const MIN_READING_PIXELS = 240;
const READING_COMPLETION_THRESHOLD = 0.95;
const ARTICLE_HEADER_OFFSET = 96;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const articleIdFromPath = (pathname = '') => {
  const match = String(pathname).match(/^\/news\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const buildArticleEntity = ({ pathname, title, image, author } = {}) => {
  const id = articleIdFromPath(pathname);
  if (!id) return null;
  return {
    id,
    type: 'article',
    title: title || 'Rastyle Article',
    author: author || undefined,
    media: image ? { image } : undefined,
    href: `/news/${encodeURIComponent(id)}`,
  };
};

const normalizeReadingSample = ({ articleTop = 0, articleHeight = 0, scrollY = 0, viewportHeight = 0 } = {}) => {
  const duration = Math.max(0, Number(articleHeight) || 0);
  const readingLine = Math.max(0, Number(scrollY) || 0) + Math.max(0, Number(viewportHeight) || 0) * 0.65;
  const position = duration > 0 ? clamp(readingLine - (Number(articleTop) || 0), 0, duration) : 0;
  return { position, duration, progress: duration > 0 ? position / duration : 0 };
};

const activeHeadingAnchor = (articleElement, readingLine = 0) => {
  if (!articleElement?.querySelectorAll) return null;
  const headings = [...articleElement.querySelectorAll('h2[id], h3[id]')];
  let active = null;
  headings.forEach((heading) => {
    const top = heading.getBoundingClientRect().top + (globalThis.scrollY || 0);
    if (top <= readingLine) active = heading.id;
  });
  return active;
};

const enqueueReadingResume = ({ entity, sample, metadata, completed }) => enqueueResumeSync({
  anonymous_id: getAnonymousId(),
  entity_id: entity.id,
  entity_type: entity.type || 'article',
  position_seconds: sample.position,
  duration_seconds: sample.duration,
  completed,
  metadata: { ...metadata, entity, updated_at: new Date().toISOString() },
});

const persistReadingSample = ({ entity, sample, anchor, metadata = {} } = {}) => {
  if (!entity?.id || !sample || sample.duration <= 0 || sample.position < MIN_READING_PIXELS) {
    return { saved: false, completed: false, ...(sample || {}) };
  }
  const readingMetadata = { ...metadata, unit: 'pixels', anchor: anchor || null };
  if (sample.progress >= READING_COMPLETION_THRESHOLD) {
    markResumeCompleted(entity);
    enqueueReadingResume({ entity, sample, metadata: readingMetadata, completed: true });
    return { saved: true, completed: true, ...sample };
  }
  saveResumeState(entity, sample.position, sample.duration, readingMetadata);
  enqueueReadingResume({ entity, sample, metadata: readingMetadata, completed: false });
  return { saved: true, completed: false, ...sample };
};

const findArticleResume = (entity, items = readResumeState()) => {
  if (!entity?.id) return null;
  return items.find((item) => item?.entity?.type === 'article' && String(item.entity.id) === String(entity.id)) || null;
};

const restoreReadingPosition = ({ item, articleTop = 0, articleHeight = 0, headerOffset = ARTICLE_HEADER_OFFSET } = {}) => {
  if (!item) return null;
  const duration = Math.max(0, Number(articleHeight) || Number(item.duration_seconds) || 0);
  if (duration <= 0) return null;
  const saved = clamp(Number(item.position_seconds) || 0, 0, duration);
  return Math.max(0, (Number(articleTop) || 0) + saved - Math.max(0, Number(headerOffset) || 0));
};

export {
  ARTICLE_HEADER_OFFSET,
  MIN_READING_PIXELS,
  READING_COMPLETION_THRESHOLD,
  activeHeadingAnchor,
  articleIdFromPath,
  buildArticleEntity,
  findArticleResume,
  normalizeReadingSample,
  persistReadingSample,
  restoreReadingPosition,
};
